import React, { useState, useEffect, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { setupFacilityListener } from './facilityHandlers';
import FORM_CONFIG from './formConfig';
import StepOne from './Steps/StepOne';
import StepTwo from './Steps/StepTwo';
import SuccessMessage from '../successMessage';
import Container from '@components/Container';
import Text from '@components/RTE/Text';
import useSiteMetadata from '@hooks/useSiteMetadata';
import { getFormattedCredentials, getFormattedSpeciality } from '../utils/payload-formatting';
import { navigate } from 'gatsby';
import GTM from '@utils/GTM';

// Analytics tracking function
const trackFormAnalytics = (eventName, data = {}) => {
    if (FORM_CONFIG.analytics.trackEvents.includes(eventName)) {
        // Implement your analytics tracking here
    }
};

// Section error checking functions
const setupSectionErrorObserver = (sectionId, setHasErrors) => {
    const checkSectionErrors = () => {
        const section = document.getElementById(sectionId);
        if (section) {
            const errorElements = section.getElementsByClassName('error-active');
            setHasErrors(errorElements.length > 0);
        }
    };

    // Initial check
    checkSectionErrors();

    // Create observer to watch for changes in error-active classes
    const observer = new MutationObserver(checkSectionErrors);
    const section = document.getElementById(sectionId);

    if (section) {
        observer.observe(section, {
            subtree: true,
            attributes: true,
            attributeFilter: ['class']
        });
    }

    return () => observer.disconnect();
};
const getText = (item) => {
    return (
        <Text
            content={item?.text?.raw}
            classOverlay={`${item?.classOverlay?.classOverlay} ${item?.textAlign === 'Center' ? 'mx-auto' : ''}`}
            marginTopDesktop={item?.marginTopDesktop}
            marginBottomDesktop={item?.marginBottomDesktop}
            marginTopMobile={item?.marginTopMobile}
            marginBottomMobile={item?.marginBottomMobile}
            paddingTopDesktop={item?.paddingTopDesktop}
            paddingBottomDesktop={item?.paddingBottomDesktop}
            paddingTopMobile={item?.paddingTopMobile}
            paddingBottomMobile={item?.paddingBottomMobile}
            paddingLeftDesktop={item?.paddingLeftDesktop}
            paddingRightDesktop={item?.paddingRightDesktop}
            paddingLeftMobile={item?.paddingLeftMobile}
            paddingRightMobile={item?.paddingRightMobile}
            maxWidthDesktop={item?.maxWidthDesktop}
            maxWidthMobile={item?.maxWidthMobile}
            textAlign={item?.textAlign}
            textColor={item?.textColor?.textColor}
            references={item?.references}
            style={item?.style}
            nodeStyles={item?.nodeStyles}
            customRenderers={item?.customRenderers}
            customMarks={item?.customMarks}
        />
    );
};

const SurgeonSelector = ({
    className,
    onSubmitSuccess,
    successBlock,
    formIntroductionHeader
}) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [hasPersonalInfoErrors, setHasPersonalInfoErrors] = useState(false);
    const [hasSpecialtyErrors, setHasSpecialtyErrors] = useState(false);
    const [hasFacilityErrors, setHasFacilityErrors] = useState(false);
    const [hasAgreementErrors, setHasAgreementErrors] = useState(false);
    const [registrationToken, setRegistrationToken] = useState(null);
    const { apiUrl } = useSiteMetadata();

    // Use an object of refs for all possible facilities
    const stateSelectRefs = useRef({});

    const methods = useForm({
        defaultValues: {
            ...FORM_CONFIG.defaultValues,
            facilities: [{ id: 1 }],
            confirmedFacilities: []
        },
        mode: 'onBlur',
        reValidateMode: 'onChange'
    });

    const {
        handleSubmit,
        reset,
        watch,
        setValue,
        trigger,
        formState: { isSubmitSuccessful },
        setError
    } = methods;

    // Add useEffect to check for section errors
    useEffect(() => {
        const personalInfoObserver = setupSectionErrorObserver(FORM_CONFIG.sections.personalInfo.id, setHasPersonalInfoErrors);
        const specialtyObserver = setupSectionErrorObserver(FORM_CONFIG.sections.specialty.id, setHasSpecialtyErrors);
        const facilityObserver = setupSectionErrorObserver(FORM_CONFIG.sections.facility.id, setHasFacilityErrors);
        const agreementObserver = setupSectionErrorObserver(FORM_CONFIG.sections.agreement.id, setHasAgreementErrors);

        return () => {
            personalInfoObserver();
            specialtyObserver();
            facilityObserver();
            agreementObserver();
        };
    }, []);

    // Listen for facility selection event
    useEffect(() => {
        return setupFacilityListener(setValue, stateSelectRefs.current);
    }, [setValue]);

    // Clear form and errors after successful submission
    useEffect(() => {

        if (isSubmitSuccessful) {
            reset();
            // Dispatch form reset event
            document.dispatchEvent(new Event('form:reset'));
        } else {
            // TODO: show error state
        }
    }, [isSubmitSuccessful, reset]);

    useEffect(() => {
        GTM.formDelivery('Surgeon Selector');
        GTM.formInitiation('Surgeon Selector');
        GTM.fieldCompletion('Surgeon Selector');
    }, []);

    const nextStep = async () => {
        // clean registration token if it exists
        setRegistrationToken(null);

        // get current form data
        const formData = watch();

        // Validate step 1 fields
        const isStepOneValid = await trigger([
            'firstName',
            'lastName',
            'email',
            'npiNumber',
        ]);

        // Track field validations for step 1
        const step1Fields = ['firstName', 'lastName', 'email', 'npiNumber', 'middleName'];
        const currentErrors = methods.formState.errors;

        step1Fields.forEach(fieldName => {
            const errorValue = currentErrors[fieldName]?.message || 'Valid';
            if (errorValue && errorValue !== 'Valid') {
                GTM.fieldValidation('Surgeon Selector', fieldName, errorValue);
            }
        });

        if (isStepOneValid) {
            trackFormAnalytics('next_step', formData);

            // make an API call for step 1; if it fails, show an error message and don't move on to step 2
            try {
                const payload = {
                    code: "form_hcp_surgeon_step_1",
                    first_name: formData.firstName,
                    middle_name: formData.middleName,
                    last_name: formData.lastName,
                    email: formData.email,
                    npi: formData.npiNumber,
                }

                const response = await fetch(`${apiUrl}/signup_surgeon`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                });

                const responseJson = await response.json();

                if (!response.ok) {
                    if (responseJson?.errors['signup step 1']?.error.toLowerCase() === 'invalid_npi') {
                        setError('npiNumber', {
                            type: 'custom',
                            message: 'Please enter the correct NPI number'
                        });
                        return; // Don't proceed to next step
                    } else {
                        navigate('/500'); // Using Gatsby's navigate instead of window.location
                        return;
                    }

                } else {
                    GTM.formSubmission('Surgeon Selector');
                    // const responseJson = await response.json();
                    setRegistrationToken(responseJson.data.registration_token);
                }

                setCurrentStep(2);
            } catch (error) {
                console.error('Error submitting form:', error);
                // TODO: show error state
            }
        }
    };

    const prevStep = () => {
        trackFormAnalytics('prev_step', watch());
        setCurrentStep(1);
    };

    const onSubmit = async (data) => {
        trackFormAnalytics('submit', data);

        const payload = {
            code: "form_hcp_surgeon_step_2",
            registration_token: registrationToken,
            age_of_patient: data.ageOfPatients,
            credentials: data.credentials.map(credential => getFormattedCredentials(credential)),
            speciality: data.specialties.map(speciality => getFormattedSpeciality(speciality)),
            facilities: data.confirmedFacilities.map(facility => ({
                hco_entity_id: facility.hco_entity_id,
                facility_name: facility.facilityName,
                address: facility.address,
                city: facility.city,
                state: facility.state,
                zip_code: facility.zipCode,
                phone: facility.phoneNumber,
                web: facility.facilityWebAddress,
                procedures: facility.procedures,
            })),
            optin_comm: data.receiveUpdates,
            consent_share: data.agreeToTerms,
            consent_acknowledge_fad: data.agreeToSubmission,
        };

        // Here we send the form data to a server
        try {
            const response = await fetch(`${apiUrl}/signup_surgeon`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error('Error submitting form', response.statusText);
            } else {
                GTM.formSubmission('Surgeon Selector');
            }
        } catch (error) {
            // TODO: handle error properly and show error state
            console.error('Error submitting form:', error);
        }

        // Call the success callback if provided
        if (onSubmitSuccess) {
            onSubmitSuccess(data);
        }

        setIsSubmitted(true);
    };

    if (isSubmitted) {
        return <SuccessMessage successBlock={successBlock} />;
    }

    return (
        <FormProvider {...methods}>
            {formIntroductionHeader && formIntroductionHeader.length > 0 && (
                <>
                    {formIntroductionHeader.map((block, blockIndex) => (
                        <Container
                            key={`header-block-${blockIndex}`}
                            backgroundImage={block.backgroundImage}
                            className={`${block.classOverlay?.classOverlay || ''}`}
                            type={block.type}
                            paddingTopDesktop={block.paddingTopDesktop}
                            paddingBottomDesktop={block.paddingBottomDesktop}
                            paddingTopMobile={block.paddingTopMobile}
                            paddingBottomMobile={block.paddingBottomMobile}
                            paddingLeftDesktop={block.paddingLeftDesktop}
                            paddingRightDesktop={block.paddingRightDesktop}
                            paddingLeftMobile={block.paddingLeftMobile}
                            paddingRightMobile={block.paddingRightMobile}

                        >
                            {block.items?.map((item, index) => (
                                <div key={`header-item-${index}`}>
                                    {item.__typename === 'ContentfulRichText' ? getText(item) : null}
                                </div>
                            ))}
                        </Container>
                    ))}
                </>
            )}
            <form
                id={FORM_CONFIG.id}
                onSubmit={async (e) => {
                    e.preventDefault(); // Prevent default form submission

                    // Run validation on all fields first for Step 2
                    const isValid = await methods.trigger();

                    // Now errors object should be populated
                    // Track all field validations after validation
                    const fields = Object.keys(FORM_CONFIG.defaultValues);
                    const currentValues = methods.getValues();
                    const currentErrors = methods.formState.errors;

                    fields.forEach(fieldName => {
                        // Track field validations for step 2 fields
                        const errorValue = currentErrors[fieldName]?.message || 'Valid';

                        if (errorValue && errorValue !== 'Valid') {
                            GTM.fieldValidation('Surgeon Selector', fieldName, errorValue);
                        }
                    });

                    // Continue with form submission
                    handleSubmit(onSubmit)(e);
                }}
                className={`max-w-[1200px] mx-6 lg:mx-auto px-0 lg:px-12 pt-12 pb-[150px] lg:pb-[218px] flex flex-col gap-8 relative ${className}`}
                aria-label="Surgeon Selector Form"
                noValidate
            >
                <div className="sr-only" id="form-description">
                    Please complete this form to register as a surgeon. The form has multiple steps. Required fields are marked with an asterisk.
                </div>

                {currentStep === 1 ? (
                    <StepOne
                        nextStep={nextStep}
                        hasPersonalInfoErrors={hasPersonalInfoErrors}
                    />
                ) : (
                    <StepTwo
                        prevStep={prevStep}
                        registrationToken={registrationToken}
                        hasSpecialtyErrors={hasSpecialtyErrors}
                        hasFacilityErrors={hasFacilityErrors}
                        hasAgreementErrors={hasAgreementErrors}
                        stateSelectRefs={stateSelectRefs.current}
                    />
                )}
            </form>
        </FormProvider>
    );
};

export default SurgeonSelector;